import * as React from 'react';
import styled from 'styled-components';

import { Honeybadger } from '@/api/app/honeybadger';
import { AppNavigatorDelegateContext } from './app-navigator.context';
import { NavigationPaneRouteParamsContext } from './navigation-pane-route-params.context';
import { PaneMeasurementsCanvas } from './pane-measurements-canvas';

export interface PaneProps {
  Content: React.ComponentType;
  primary: boolean;
  width: number;
  height: number;
  routeParams: Record<string, string>;
  showMeasurements?: boolean;
  ErrorComponent?: React.ComponentType<{ width: number, height: number }>;
}

export interface PaneState {
  error: boolean;
  errorDimensions: [width: number, height: number];
}

const PaneContent = React.memo(({
  Content, ErrorComponent, error, errorDimensions: [width, height],
}: PaneState&Pick<PaneProps, 'Content'|'ErrorComponent'>) => (
  <>
    {!error && Content && <Content />}
    {error && ErrorComponent && <ErrorComponent width={width} height={height} />}
  </>
));

export const PaneContainer = styled.div<{ showMeasurements: boolean }>`
  nth-child(${({ showMeasurements }) => (showMeasurements ? 1 : 2)}) {
    visibility: hidden;
  }
`;

export class Pane extends React.Component<PaneProps, PaneState> {
  static displayName: string;

  static contextType = AppNavigatorDelegateContext;

  static readonly dependencies = ['Content', 'primary', 'routeParams', 'showMeasurements', 'ErrorComponent'];

  declare context: React.ContextType<typeof AppNavigatorDelegateContext>;

  ['constructor']!: typeof Pane;

  constructor(props) {
    super(props);
    this.state = { error: false, errorDimensions: [null, null] };
  }

  componentDidMount() {
    this.maybeRegisterOrDeregisterAsPrimary();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state !== nextState) return true;
    // eslint-disable-next-line react/destructuring-assignment
    return this.constructor.dependencies.some((v) => this.props[v] !== nextProps[v]);
  }

  componentDidUpdate(prevProps: PaneProps, prevState: PaneState) {
    this.maybeRegisterOrDeregisterAsPrimary(prevProps);

    if (!prevState.error) return;
    const { Content, routeParams } = this.props;
    if (Content === prevProps.Content && routeParams === prevProps.routeParams) return;
    const navigator = this.context;
    navigator.deregisterError(this);
    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({ error: false, errorDimensions: [null, null] });
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Honeybadger.notify(error, { context: info });

    const { error: previousError } = this.state;
    if (previousError) throw error; // internal error or error in error component, nothing we can do

    const { ErrorComponent, width, height } = this.props;
    if (!ErrorComponent) throw error; // If no ErrorComponent specified, do not attempt to handle
    if (!(width > 0 && height > 0)) throw error; // If missing dimensions, do not attempt to handle

    const navigator = this.context;
    navigator.registerError(this, error);
    this.setState({ error: true, errorDimensions: [width, height] });
  }

  getContent() {
    const { Content } = this.props;
    return Content;
  }

  private maybeRegisterOrDeregisterAsPrimary(prevProps?: PaneProps) {
    const navigator = this.context;
    const { primary } = this.props;
    if (primary) {
      if (!prevProps?.primary) navigator.registerPrimaryPane(this);
    } else if (prevProps?.primary) navigator.deregisterPrimaryPane(this);
  }

  render() {
    const { error, errorDimensions } = this.state;
    const { Content, ErrorComponent, routeParams, showMeasurements } = this.props;

    if (typeof Content === 'undefined') return null;

    return (
      <NavigationPaneRouteParamsContext.Provider value={routeParams}>
        <PaneContainer
          showMeasurements={showMeasurements}
          className="pane-container measurements-container"
        >
          <PaneContent
            Content={Content}
            ErrorComponent={ErrorComponent}
            error={error}
            errorDimensions={errorDimensions}
          />
          <PaneMeasurementsCanvas
            className="measurements-canvas"
            visible={showMeasurements}
          />
        </PaneContainer>
      </NavigationPaneRouteParamsContext.Provider>
    );
  }
}
